import { Link, type LinkProps } from '@tanstack/react-router';
import { ChevronRight } from 'lucide-react';
import type { ReactNode } from 'react';
import { cn } from '../../lib/utils';

export interface Breadcrumb {
  label: ReactNode;
  /** Route link options (`{ to, params }`); the last crumb usually has none. */
  link?: Pick<LinkProps, 'to' | 'params' | 'search'>;
}

export interface PageHeaderProps {
  title: ReactNode;
  description?: ReactNode;
  breadcrumbs?: readonly Breadcrumb[];
  /** Right-aligned actions (buttons, menus). */
  actions?: ReactNode;
  /** Badges/pills shown next to the title. */
  meta?: ReactNode;
  className?: string;
  children?: ReactNode;
}

export function PageHeader({
  title,
  description,
  breadcrumbs,
  actions,
  meta,
  className,
  children,
}: PageHeaderProps) {
  return (
    <header className={cn('space-y-3', className)} data-slot="page-header">
      {breadcrumbs && breadcrumbs.length > 0 ? (
        <nav aria-label="Breadcrumb">
          <ol className="flex flex-wrap items-center gap-1 text-fg-muted text-sm">
            {withKeys(breadcrumbs).map(({ crumb, key, first }) => (
              <li key={key} className="flex items-center gap-1">
                {first ? null : <ChevronRight className="size-3.5" aria-hidden="true" />}
                {crumb.link ? (
                  <Link {...crumb.link} className="hover:text-fg hover:underline">
                    {crumb.label}
                  </Link>
                ) : (
                  <span className="text-fg-muted">{crumb.label}</span>
                )}
              </li>
            ))}
          </ol>
        </nav>
      ) : null}
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0 space-y-1">
          <div className="flex flex-wrap items-center gap-3">
            <h1 className="font-semibold text-2xl text-fg tracking-tight">{title}</h1>
            {meta}
          </div>
          {description ? <p className="max-w-3xl text-fg-muted">{description}</p> : null}
        </div>
        {actions ? (
          <div className="flex shrink-0 flex-wrap items-center gap-2">{actions}</div>
        ) : null}
      </div>
      {children}
    </header>
  );
}

/** Stable keys: the link target when there is one, else the position (crumbs never reorder). */
const withKeys = (crumbs: readonly Breadcrumb[]) =>
  crumbs.map((crumb, position) => ({
    crumb,
    first: position === 0,
    key: crumb.link ? `${String(crumb.link.to)}:${position}` : `crumb:${position}`,
  }));

/** Standard page wrapper: max width + vertical rhythm. */
export function Page({ className, children }: { className?: string; children: ReactNode }) {
  return <div className={cn('mx-auto w-full max-w-6xl space-y-6', className)}>{children}</div>;
}
